import { PopUpManager,  WizPopUp, WizTranslationManager } from "@wizeoo/akm-component-lib";
import { LoadinPop } from "../../views/main/loading/loading.pop";
import { COMPUTE_MODE, DataLoadObj, FeiVO, WizConstants } from "@wizeoo/akm-compute-lib";
import { Model } from "../../model/model";

/** un jeu de données de retour pour transmission au widgets */
export interface FeiMessage {
  vision: string; // la vision (pour les crossed)
  level: number; // niveau du fei
  label: string; // libellé du step
  fei?: FeiVO; // fei porteur
  status: number; // statut en cours (remote, local, calcul, fini...)
  totalLines?: number; // nb baselines
  totalCells?: number; // nb cellules

  totalLocalTime?: number; // temps total de chargement local
  localLinesLoaded: number; // nb lignes chargées depuis le cache local

  totalRemoteTime?: number; // temps total de chargement distant
  
  remoteLinesLoaded?: number; // nb lignes remotes chargées
  totalRemoteLines?: number;
  
  remoteCellsLoaded?: number; // nb values remotes chargées
  totalRemoteCells?: number;

  totalComputeTime?: number; // temps de calcul
}


/**
 * génération d'un message intelligible lors de la construction des FEIs
 */
export class DataMessage {
  
  /** les constantes d'état */
  public static LOAD_LOCAL_INIT: number = 1;
  public static LOAD_LOCAL_ENDED: number = 2;
  public static LOAD_REMOTE_INIT: number = 4;
  public static LOAD_REMOTE_RUNNING: number = 8;
  public static LOAD_REMOTE_ENDED: number = 16;
  public static COMPUTE_INIT: number = 32;
  public static COMPUTE_REMOTE_INIT: number = 64;
  public static COMPUTE_ENDED: number = 128;
  public static COMPUTE_REMOTE_ENDED: number = 256;
  public static ENDED: number = 512;

  protected model: Model = null;

  constructor(model: Model) {
    this.model = model;
  } 

  /** l'instance de PopUp */
  private dataMessagePop: WizPopUp;
  /**
   * l'instance du contenu de la popUp (LoadingPop)
   * alimenté par LoadingPop sur show
   */
  public loadingPop: LoadinPop = null;

  private  _stateMap: Map<number, string> = null;
  public getStateLabel(state: number): string {
    let result: string;
    if(!this._stateMap) {
      this._stateMap = new Map();
      this._stateMap.set( DataMessage.LOAD_LOCAL_INIT, '$.loading.state.localinit.$');
      this._stateMap.set( DataMessage.LOAD_LOCAL_ENDED, '$.loading.state.localended.$');
      this._stateMap.set( DataMessage.LOAD_REMOTE_INIT, '$.loading.state.remoteinit.$');
      this._stateMap.set( DataMessage.LOAD_REMOTE_RUNNING, '$.loading.state.remoterunning.$');
      this._stateMap.set( DataMessage.LOAD_REMOTE_ENDED, '$.loading.state.remoteended.$');
      this._stateMap.set( DataMessage.COMPUTE_INIT, '$.loading.state.computeinit.$');
      this._stateMap.set( DataMessage.COMPUTE_ENDED, '$.loading.state.computeended.$');
      this._stateMap.set( DataMessage.COMPUTE_REMOTE_INIT, '$.loading.state.compute.remote.init.$');  
      this._stateMap.set( DataMessage.COMPUTE_REMOTE_ENDED, '$.loading.state.compute.remote.ended.$');
      this._stateMap.set( DataMessage.ENDED, '$.loading.state.ended.$');
    }

    result = WizTranslationManager.getInstance().translateKey(this._stateMap.get(state));
    return result;
  }


  /** la map de message, par fei.lvl */
  public messageMap: Map<string, Map<string, FeiMessage>> = new Map();

  private newMessage(kind: number, content: DataLoadObj) : FeiMessage {
    let label :string = this.model.visionEngine.getStepLabel(content.visionStep);
    
    if(content.scenario !== WizConstants.BASE_SCENARIO) {
      label += WizConstants.NICE_KEY_SEPARATOR + content.scenario;
    }

    if(!!content.crossed) {
      label = "CROSS: " + content.visionStep.code;
    }

    let res: FeiMessage = {
      vision: content.visionStep.visionCode,
      level: content.visionStep.level, // niveau du fei
      label: label,
      status: kind,
      localLinesLoaded: 0
    }
    return res;
  }

  /**
   * mise à jour du jeu de message
   * @param kind type d'événement (ci-dessus)
   * @param dlo DataLoadObj
   * @param scen string sur compute
   */
  public doMessage(kind: number, dlo: DataLoadObj): void { 
    
    let mess: FeiMessage;
    let fei: FeiVO;
    
    let key: string = [dlo.visionStep.visionCode, dlo.visionStep.level].join(WizConstants.CONTEXT_SEPARATOR);

    if (kind === DataMessage.LOAD_LOCAL_INIT || kind === DataMessage.COMPUTE_REMOTE_INIT) {
      if (!this.messageMap.has(key)) {
        this.messageMap.set(key, new Map());
      }
      
      this.messageMap.get(key).set(dlo.scenario, this.newMessage(kind, dlo));
      // console.log('doMessage', key, dlo, this.messageMap);
    }

    try {
      mess = this.messageMap.get(key).get(dlo.scenario);
    } catch(e: any) {
      console.log('doMessageError',this.messageMap, dlo.visionStep.level);
      return;
    }

    
    if (kind >= mess.status) { // ça calcule trop vite...
      mess.status = kind;
      switch (kind) {

        case DataMessage.LOAD_LOCAL_ENDED:
          mess.localLinesLoaded = Object.values(dlo.localBaselines).length;
          mess.totalLocalTime = dlo.totalLocalTime;
          break;
        case DataMessage.LOAD_REMOTE_INIT:
          mess.totalRemoteLines = dlo.nbLines;
          mess.totalRemoteCells = dlo.nbValues;
          mess.remoteLinesLoaded = 0;
          mess.remoteCellsLoaded = 0;
          break;
        case DataMessage.LOAD_REMOTE_RUNNING:
          mess.remoteLinesLoaded = dlo.loadedRemoteLines; // Object.values(dlo.remoteBaselines).length;
          mess.remoteCellsLoaded = dlo.loadedRemoteValues; // dlo.remoteBaselineValues.length;
          break;
        case DataMessage.LOAD_REMOTE_ENDED:
          mess.totalRemoteTime = dlo.totalRemoteTime;
          break;
        case DataMessage.COMPUTE_INIT:
          fei = this.model.dataEngine.getFei(COMPUTE_MODE.LOCAL, dlo.visionStep.level, dlo.scenario);
          mess.totalLines = [...fei.lines.get(fei.scenario).keys()].length;
          mess.totalCells = mess.totalLines * fei.orderedMatrix.length;
          mess.status = DataMessage.COMPUTE_INIT;
          break;
        case DataMessage.COMPUTE_ENDED:
          mess.status = DataMessage.COMPUTE_ENDED;
          mess.totalComputeTime = dlo.totalComputeTime;
          break;
        case DataMessage.COMPUTE_REMOTE_INIT:
          mess.remoteLinesLoaded = 0;
          mess.remoteCellsLoaded = 0;
          break;
        case DataMessage.COMPUTE_REMOTE_ENDED:
          mess.remoteLinesLoaded = dlo.loadedRemoteLines;
          mess.remoteCellsLoaded = dlo.loadedRemoteValues;
          mess.totalRemoteTime = dlo.totalRemoteTime;
          break;
      }
    }

    let small: string = this.smallMessage(mess);
    // console.log('datamess', small);


    this.showPop(true);
    // this.content?.feedContent();
    this.loadingPop?.addMessage(small);

    setTimeout(() => {
      let allEnded: boolean = true;
      [...this.messageMap.keys()].forEach(level => {
        [...this.messageMap.get(level).values()].forEach((mess: FeiMessage) => {
          if(mess.status < DataMessage.COMPUTE_ENDED) {
            // console.log('not ended', mess.vision, mess.label, mess.status)
            allEnded = false;
          }
        });
      })

      if(allEnded) { // fermeture auto
        this.showPop(false);
        this.messageMap = new Map(); // on rince la map
      }
    }, 0);
  }

  /**
   * afficher / masquer la popUp si nécessaire
   * @param show 
   */
  private showPop(show:boolean): void {
    if(show) {
      if (!PopUpManager.exist(this.dataMessagePop)) {
        const targetPosition: any = { right: '0px', bottom: '50px' }; // position physique
        this.dataMessagePop = new WizPopUp( // la taille sera donnée par le style (calc)
          LoadinPop,
          false,
          false,
          null,
          '700px', // 350px',
          '150px', // calc(100% - 81px)',
          this, // params
          null,
          targetPosition
        ).show();
      }
    } else {
      if(PopUpManager.exist(this.dataMessagePop)) {
        this.dataMessagePop.hide();
      }
    }
  }


  /** Ajout d'un message en mode small */
  public smallMessage(feiMess: FeiMessage): string {

    if(!feiMess) {
      return '';
    }

    let title: string = feiMess.label + ': ' + this.getStateLabel(feiMess.status);
    let localMess: string = null;

    switch(feiMess.status) {
      case DataMessage.LOAD_LOCAL_INIT:
        if(feiMess.localLinesLoaded > 0) {
          localMess = WizTranslationManager.getInstance().translateKey('$.loading.count.locallines.$');
          localMess += ': ' + '0' + ' / ' +  feiMess.localLinesLoaded.toString();
        }

      break;
      case DataMessage.LOAD_LOCAL_ENDED:
        if(feiMess.localLinesLoaded > 0) {
          localMess = WizTranslationManager.getInstance().translateKey('$.loading.count.locallines.$');
          localMess += ': ' +  feiMess.localLinesLoaded.toString();
          localMess += ' (' +  feiMess.totalLocalTime.toString() + ' ' + WizTranslationManager.getInstance().translateKey('$.common.second.symbol.$') + ')';  
        }
      break;
      case DataMessage.LOAD_REMOTE_INIT:
        if(feiMess.totalRemoteLines + feiMess.totalRemoteCells > 0) {
          localMess = WizTranslationManager.getInstance().translateKey('$.loading.count.remotelines.$');
          localMess += ': ' + '0' + ' / ' +  feiMess.totalRemoteLines.toString();
          localMess += ' | ';
          localMess += WizTranslationManager.getInstance().translateKey('$.loading.count.remotecells.$');
          localMess += ': ' + '0' + ' / ' +  feiMess.totalRemoteCells.toString();
        }
      break;
      case DataMessage.LOAD_REMOTE_RUNNING:
        if(feiMess.totalRemoteLines + feiMess.totalRemoteCells > 0) {
          localMess = WizTranslationManager.getInstance().translateKey('$.loading.count.remotelines.$');
          localMess += ': ' + feiMess.remoteLinesLoaded.toString() + ' / ' +  feiMess.totalRemoteLines.toString();
          localMess += ' | ';
          localMess += WizTranslationManager.getInstance().translateKey('$.loading.count.remotecells.$');
          localMess += ': ' + feiMess.remoteCellsLoaded.toString() + ' / ' +  feiMess.totalRemoteCells.toString();
        }
      break;
      case DataMessage.LOAD_REMOTE_ENDED:
        if(feiMess.totalRemoteLines + feiMess.totalRemoteCells > 0) {
          localMess = WizTranslationManager.getInstance().translateKey('$.loading.count.remotelines.$');
          localMess += ': ' + feiMess.remoteLinesLoaded.toString() + ' / ' + feiMess.totalRemoteLines.toString();
          localMess += ' | ';

          localMess += WizTranslationManager.getInstance().translateKey('$.loading.count.remotecells.$');
          localMess += ': ' + feiMess.remoteCellsLoaded.toString() + ' / ' + feiMess.totalRemoteCells.toString();
          // mesure du temps sur les lignes
          localMess += ' (' +  feiMess.totalRemoteTime.toString() + ' ' + WizTranslationManager.getInstance().translateKey('$.common.second.symbol.$') + ')';
        }
      break;
      case DataMessage.COMPUTE_INIT:
        if(feiMess.totalLines + feiMess.totalCells > 0) {
          localMess = WizTranslationManager.getInstance().translateKey('$.loading.count.computelines.$');
          localMess += ': ' + '0' + ' / ' +  feiMess.totalLines.toString();
          localMess += ' | ';
          localMess += WizTranslationManager.getInstance().translateKey('$.loading.count.computecells.$');
          localMess += ': ' + '0' + ' / ' +  feiMess.totalCells.toString();
        }
      break;
      case DataMessage.COMPUTE_ENDED:
        if(feiMess.totalLines + feiMess.totalCells > 0) {
          localMess = WizTranslationManager.getInstance().translateKey('$.loading.count.computelines.$');
          localMess += ': ' + feiMess.totalLines.toString();
          localMess += ' | ';
          localMess += WizTranslationManager.getInstance().translateKey('$.loading.count.computecells.$');
          localMess += ': ' + feiMess.totalCells.toString();
          // mesure du temps de compute
          localMess += ' (' +  feiMess.totalComputeTime.toString() + ' ' + WizTranslationManager.getInstance().translateKey('$.common.second.symbol.$') + ')';  
        }
      break;
      case DataMessage.COMPUTE_REMOTE_ENDED:
        if(feiMess.remoteLinesLoaded + feiMess.remoteCellsLoaded > 0) {
          localMess = WizTranslationManager.getInstance().translateKey('$.loading.count.compute.remote.lines.$');
          localMess += ': ' + feiMess.remoteLinesLoaded.toString();
          localMess += ' | ';
          localMess += WizTranslationManager.getInstance().translateKey('$.loading.count.compute.remote.cells.$');
          localMess += ': ' + feiMess.remoteCellsLoaded.toString();
          // mesure du temps de chargement
          localMess += ' (' +  feiMess.totalRemoteTime.toString() + ' ' + WizTranslationManager.getInstance().translateKey('$.common.second.symbol.$') + ')';  

        }
      break;
    }

    if (title) {
      if(localMess){
        return title + ' | ' + localMess;
      }
      return title;
    }
    return '';
  }

}
